import React, { FC, useState, useMemo } from 'react';
import clsx from 'clsx';
import { ChevronDown, DoubleCaret } from '@td-client-frontend/icons';
import { Table } from './ActivityDetailTableBody';
import { AuditDetail } from '../../../@types/Audit';
import { DefaultColumnType } from '@td-client-frontend/common';

interface ActivityDetailTableProps {
  tableData: AuditDetail[];
  dataType: string;
}

interface ExpandAllButtonProps {
  toggleAllRowsExpanded: (arg: boolean) => void;
  isAllSubRowsExpanded: boolean;
  setAllSubRowsExpanded: (arg: boolean) => void;
}

const Expand: FC<{ expanded: boolean }> = ({ expanded }) => {
  return (
    <ChevronDown className={clsx('w-5 h-5 transform inline ml-2', expanded ? '-rotate-180' : 'rotate-0')} aria-hidden />
  );
};

const ExpandAllButton = ({
  toggleAllRowsExpanded,
  isAllSubRowsExpanded,
  setAllSubRowsExpanded
}: ExpandAllButtonProps) => {
  const onClick = () => {
    setAllSubRowsExpanded(!isAllSubRowsExpanded);
    toggleAllRowsExpanded(!isAllSubRowsExpanded);
  };
  return (
    <button data-testid="activity-detail-expand" onClick={onClick}>
      <DoubleCaret
        className={clsx(
          'fill-current text-black ml-2 w-5 h-5 transform',
          isAllSubRowsExpanded ? ' -rotate-180' : 'rotate-0'
        )}
      />
    </button>
  );
};

const ActivityDetailTable: FC<ActivityDetailTableProps> = ({ tableData, dataType }) => {
  const [isAllSubRowsExpanded, setAllSubRowsExpanded] = useState<boolean>(false);

  const basicColumnConfig: Record<string, DefaultColumnType> = {
    create: [
      {
        // Build our expander column
        id: 'expander', // Make sure it has an ID
        Header: ({ toggleAllRowsExpanded }) => {
          return (
            <ExpandAllButton
              toggleAllRowsExpanded={toggleAllRowsExpanded}
              isAllSubRowsExpanded={isAllSubRowsExpanded}
              setAllSubRowsExpanded={setAllSubRowsExpanded}
            />
          );
        },
        Cell: ({ row }) => {
          return row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  paddingLeft: `${row.depth * 3}rem`
                }
              })}
            >
              <Expand expanded={!!row.isExpanded} />
            </span>
          ) : (
            ''
          );
        }
      },
      {
        Header: 'Section',
        accessor: 'prop',
        Cell: ({ row }) => {
          return row.original.prop ? (
            <span
              className={clsx('font-effra text-md font-medium', row.id.length === 1 ? 'text-gray-45' : 'text-gray-60')}
            >
              {row.original.prop}
            </span>
          ) : (
            ''
          );
        }
      },
      {
        Header: 'Field',
        accessor: 'field'
      },
      {
        Header: 'New Value',
        accessor: 'after'
      }
    ],
    update: [
      {
        // Build our expander column
        id: 'expander', // Make sure it has an ID
        Header: ({ toggleAllRowsExpanded }) => {
          return (
            <ExpandAllButton
              toggleAllRowsExpanded={toggleAllRowsExpanded}
              isAllSubRowsExpanded={isAllSubRowsExpanded}
              setAllSubRowsExpanded={setAllSubRowsExpanded}
            />
          );
        },
        Cell: ({ row }) => {
          return row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  paddingLeft: `${row.depth * 3}rem`
                }
              })}
            >
              <Expand expanded={!!row.isExpanded} />
            </span>
          ) : (
            ''
          );
        }
      },
      {
        Header: 'Section',
        accessor: 'prop',
        Cell: ({ row }) => {
          return row.original.prop ? (
            <span
              className={clsx('font-effra text-md font-medium', row.id.length === 1 ? 'text-gray-45' : 'text-gray-60')}
            >
              {row.original.prop}
            </span>
          ) : (
            ''
          );
        }
      },
      {
        Header: 'Field',
        accessor: 'field'
      },
      {
        Header: 'Old value',
        accessor: 'before'
      },
      {
        Header: 'New Value',
        accessor: 'after'
      }
    ],
    delete: [
      {
        // Build our expander column
        id: 'expander', // Make sure it has an ID
        Header: ({ toggleAllRowsExpanded }) => {
          return (
            <ExpandAllButton
              toggleAllRowsExpanded={toggleAllRowsExpanded}
              isAllSubRowsExpanded={isAllSubRowsExpanded}
              setAllSubRowsExpanded={setAllSubRowsExpanded}
            />
          );
        },
        Cell: ({ row }) => {
          return row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  paddingLeft: `${row.depth * 3}rem`
                }
              })}
            >
              <Expand expanded={!!row.isExpanded} />
            </span>
          ) : (
            ''
          );
        }
      },
      {
        Header: 'Section',
        accessor: 'prop',
        Cell: ({ row }) => {
          return row.original.prop ? (
            <span
              className={clsx('font-effra text-md font-medium', row.id.length === 1 ? 'text-gray-45' : 'text-gray-60')}
            >
              {row.original.prop}
            </span>
          ) : (
            ''
          );
        }
      },
      {
        Header: 'Field',
        accessor: 'field'
      },
      {
        Header: 'Old value',
        accessor: 'before'
      }
    ]
  };

  const modalTableConfig: Record<string, DefaultColumnType> = useMemo(() => {
    return {
      'Create': basicColumnConfig.create,
      'Update': basicColumnConfig.update,
      'Draft Create': basicColumnConfig.create,
      'Draft Update': basicColumnConfig.update,
      'Draft Delete': basicColumnConfig.delete,
      'MarketingProfileCreate': basicColumnConfig.create,
      'MemberCreate': basicColumnConfig.create,
      'MemberUpdate': basicColumnConfig.update,
      'default': basicColumnConfig.update
    };
  }, [basicColumnConfig]);

  return (
    <Table
      columns={modalTableConfig[dataType] || modalTableConfig.default}
      data={tableData}
      setAllSubRowsExpanded={setAllSubRowsExpanded}
    />
  );
};

export default ActivityDetailTable;
